import { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";
import { Buildings, FloppyDisk, Palette, Signature } from "@phosphor-icons/react";

const PRESET_COLORS = [
  { name: "Terracotta (défaut)", hex: "#c14a2c" },
  { name: "Indigo profond", hex: "#1a2a6c" },
  { name: "Savane", hex: "#5c8a5c" },
  { name: "Ocre", hex: "#d19420" },
  { name: "Bordeaux", hex: "#8b1a1a" },
  { name: "Bleu roi", hex: "#1e4d8c" },
  { name: "Vert émeraude", hex: "#0e7554" },
  { name: "Violet royal", hex: "#5b2c8e" },
];

const resolveUrl = (u) => u ? (u.startsWith("http") || u.startsWith("data:") ? u : `${API_BASE.replace("/api", "")}${u}`) : null;

export default function Setup() {
  const { school, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", address: "", phone: "", email: "",
    currency: "XOF", academic_year: "2025-2026", motto: "",
    logo_url: "", welcome_image_url: "",
    primary_color: "#c14a2c", signature_url: "", stamp_url: "", director_title: "Le Directeur",
    bulletin_title: "", bulletin_header_color: "", bulletin_mentions: [],
    bulletin_show_stamp: true, bulletin_show_auto_signature: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (school) setForm({
      name: school.name || "", address: school.address || "", phone: school.phone || "",
      email: school.email || "", currency: school.currency || "XOF",
      academic_year: school.academic_year || "2025-2026", motto: school.motto || "",
      logo_url: school.logo_url || "", welcome_image_url: school.welcome_image_url || "",
      primary_color: school.primary_color || "#c14a2c",
      signature_url: school.signature_url || "",
      stamp_url: school.stamp_url || "",
      director_title: school.director_title || "Le Directeur",
      bulletin_title: school.bulletin_title || "",
      bulletin_header_color: school.bulletin_header_color || "",
      bulletin_mentions: Array.isArray(school.bulletin_mentions) ? school.bulletin_mentions : [],
      bulletin_show_stamp: school.bulletin_show_stamp !== false,
      bulletin_show_auto_signature: school.bulletin_show_auto_signature !== false,
    });
  }, [school]);

  const save = async () => {
    setSaving(true);
    try {
      if (school) {
        await api.patch(`/schools/${school.id}`, form);
        toast.success("Établissement mis à jour");
      } else {
        await api.post("/schools", form);
        toast.success("Établissement créé + plan comptable OHADA initialisé");
      }
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Configuration</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Établissement & identité visuelle</h1>
      </header>

      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: form.primary_color, color: "#fff" }}>
            {form.logo_url ? <img src={resolveUrl(form.logo_url)} alt="" className="w-full h-full object-cover" /> : <Buildings size={26} weight="duotone" />}
          </div>
          <div>
            <div className="font-heading text-lg font-semibold">{school ? school.name : "Nouvel établissement"}</div>
            <div className="text-xs text-muted-foreground">{school ? `ID · ${school.id.slice(0,8)}…` : "Renseignez les informations ci-dessous"}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Nom</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="input-school-name" /></div>
          <div><Label>Devise</Label><Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} data-testid="input-school-currency" /></div>
          <div><Label>Année scolaire</Label><Input value={form.academic_year} onChange={e => setForm({ ...form, academic_year: e.target.value })} data-testid="input-school-year" /></div>
          <div className="md:col-span-2"><Label>Téléphone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Devise / Motto</Label><Input value={form.motto} onChange={e => setForm({ ...form, motto: e.target.value })} /></div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-4 flex items-center gap-2">
            <Palette size={14} weight="fill" /> Couleur d'accent (boutons, PDFs, portail)
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {PRESET_COLORS.map(c => (
              <button
                key={c.hex} type="button" onClick={() => setForm({ ...form, primary_color: c.hex })}
                className={`w-10 h-10 rounded-lg border-2 transition-all ${form.primary_color === c.hex ? "border-foreground scale-110" : "border-border hover:border-muted-foreground"}`}
                style={{ backgroundColor: c.hex }} title={c.name}
                data-testid={`color-${c.hex.slice(1)}`}
              />
            ))}
            <div className="flex items-center gap-2 ml-2">
              <Label className="text-xs whitespace-nowrap">Personnalisée</Label>
              <input type="color" value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })} className="w-12 h-10 rounded border border-border cursor-pointer" data-testid="color-picker" />
              <Input value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })} className="w-28 font-mono text-xs" data-testid="input-color-hex" />
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-4">Identité visuelle</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Logo</Label>
              <PhotoUploader value={form.logo_url} onChange={v => setForm({ ...form, logo_url: v })} kind="logo" size={100} shape="square" label="Apparaît sur bulletins, reçus et interface" />
            </div>
            <div>
              <Label className="mb-2 block">Image d'accueil (bannière)</Label>
              <PhotoUploader value={form.welcome_image_url} onChange={v => setForm({ ...form, welcome_image_url: v })} kind="welcome" size={100} shape="square" label="Affichée aux parents à la connexion" />
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-4 flex items-center gap-2">
            <Signature size={14} weight="duotone" /> Bulletin personnalisé
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Titre du bulletin (optionnel)</Label>
              <Input value={form.bulletin_title} onChange={e => setForm({ ...form, bulletin_title: e.target.value })} placeholder="BULLETIN DE NOTES" data-testid="input-bulletin-title" />
              <p className="text-xs text-muted-foreground mt-1">Ex: « RELEVÉ SEMESTRIEL » · vide = défaut plateforme</p>
            </div>
            <div>
              <Label>Couleur d'en-tête tableau (optionnel)</Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={form.bulletin_header_color || "#1a2a6c"} onChange={e => setForm({ ...form, bulletin_header_color: e.target.value })} className="w-12 h-10 rounded border border-border cursor-pointer" data-testid="color-bulletin-header" />
                <Input value={form.bulletin_header_color} onChange={e => setForm({ ...form, bulletin_header_color: e.target.value })} placeholder="#1a2a6c" className="w-32 font-mono text-xs" />
                <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, bulletin_header_color: "" })}>Réinit.</Button>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Label>Mentions personnalisées (optionnel)</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">Vide = mentions par défaut (Très Bien 16, Bien 14, Assez Bien 12, Passable 10, Insuffisant &lt;10).</p>
            <div className="space-y-2">
              {(form.bulletin_mentions || []).map((m, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <Input type="number" step="0.5" value={m.min ?? ""} onChange={e => {
                    const arr = [...form.bulletin_mentions]; arr[idx] = { ...arr[idx], min: parseFloat(e.target.value) || 0 }; setForm({ ...form, bulletin_mentions: arr });
                  }} placeholder="Seuil" className="col-span-2" data-testid={`mention-min-${idx}`} />
                  <Input value={m.label_fr || ""} onChange={e => {
                    const arr = [...form.bulletin_mentions]; arr[idx] = { ...arr[idx], label_fr: e.target.value }; setForm({ ...form, bulletin_mentions: arr });
                  }} placeholder="Label FR" className="col-span-4" data-testid={`mention-fr-${idx}`} />
                  <Input value={m.label_en || ""} onChange={e => {
                    const arr = [...form.bulletin_mentions]; arr[idx] = { ...arr[idx], label_en: e.target.value }; setForm({ ...form, bulletin_mentions: arr });
                  }} placeholder="Label EN" className="col-span-4" data-testid={`mention-en-${idx}`} />
                  <Button type="button" size="sm" variant="ghost" onClick={() => {
                    const arr = form.bulletin_mentions.filter((_, i) => i !== idx); setForm({ ...form, bulletin_mentions: arr });
                  }} className="col-span-2 text-destructive">Retirer</Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, bulletin_mentions: [...(form.bulletin_mentions || []), { min: 10, label_fr: "", label_en: "" }] })} data-testid="btn-add-mention">
                + Ajouter une mention
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-4 flex items-center gap-2">
            <Signature size={14} weight="duotone" /> Signature & cachet du directeur (bulletins PDF)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer" data-testid="toggle-show-stamp-wrap">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={!!form.bulletin_show_stamp}
                onChange={e => setForm({ ...form, bulletin_show_stamp: e.target.checked })}
                data-testid="toggle-show-stamp"
              />
              <span className="text-sm">
                <span className="font-medium">Afficher le cachet rond</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Décoche pour retirer le cachet officiel des bulletins PDF.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer" data-testid="toggle-show-auto-sig-wrap">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={!!form.bulletin_show_auto_signature}
                onChange={e => setForm({ ...form, bulletin_show_auto_signature: e.target.checked })}
                data-testid="toggle-show-auto-signature"
              />
              <span className="text-sm">
                <span className="font-medium">Signature stylisée automatique</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Décoche si tu ne veux la signature scannée. Si l'upload est vide et que cette case est décochée, seul le titre « {form.director_title || "Le Directeur"} » apparaîtra.
                </span>
              </span>
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div>
              <Label className="mb-2 block">Signature scannée</Label>
              <PhotoUploader value={form.signature_url} onChange={v => setForm({ ...form, signature_url: v })} kind="signature" size={100} shape="square" label="PNG transparent recommandé · apposée automatiquement" />
            </div>
            <div>
              <Label className="mb-2 block">Cachet officiel (rond)</Label>
              <PhotoUploader value={form.stamp_url} onChange={v => setForm({ ...form, stamp_url: v })} kind="stamp" size={100} shape="circle" label="PNG rond transparent · remplace le cachet auto-généré" />
            </div>
            <div>
              <Label>Titre affiché sous la signature</Label>
              <Input value={form.director_title} onChange={e => setForm({ ...form, director_title: e.target.value })} placeholder="Le Directeur" data-testid="input-director-title" />
              <p className="text-xs text-muted-foreground mt-2">Ex: « Le Directeur », « La Directrice », « Le Proviseur »…</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-full text-white" style={{ backgroundColor: form.primary_color }} data-testid="btn-save-school">
            <FloppyDisk size={16} className="mr-2" /> {saving ? "Enregistrement…" : (school ? "Mettre à jour" : "Créer l'établissement")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
